import { clampToMonthRange, getDateFromMonthKey, isValidDate } from 'lib/datetime/dateUtils'

/** Capturing `YYYY-MM` pattern shared by calendar focus helpers and trip parsing. */
export const CALENDAR_MONTH_KEY_REGEX = /^(\d{4})-(0[1-9]|1[0-2])$/

/**
 * Which month a calendar should open on, as a `YYYY-MM` key or any date within
 * that month. Always a viewport hint — it never selects a date.
 */
export type CalendarFocusMonth = string | Date

/** Normalises a focus month hint to a Date, or undefined when it is unusable. */
export function toCalendarFocusDate(focusMonth?: CalendarFocusMonth): Date | undefined {
  if (!focusMonth) return undefined

  if (typeof focusMonth === 'string') {
    const match = CALENDAR_MONTH_KEY_REGEX.exec(focusMonth)
    if (!match) return undefined

    const date = getDateFromMonthKey(focusMonth)
    // Reject years that JS silently remaps (0–99 → 1900–1999).
    if (!isValidDate(date) || date.getFullYear() !== Number(match[1])) return undefined
    return date
  }

  return isValidDate(focusMonth) ? focusMonth : undefined
}

/** Normalises a focus month hint and pulls it into the calendar's selectable range. */
export function clampCalendarFocusMonth(
  focusMonth: CalendarFocusMonth | undefined,
  min?: Date,
  max?: Date,
): Date | undefined {
  const date = toCalendarFocusDate(focusMonth)
  return date ? clampToMonthRange(date, min, max) : undefined
}

/**
 * Viewport month for vertical (mobile) calendars.
 *
 * Selected start wins over an indicative/focus hint so already-picked dates
 * stay on screen; the hint applies only when nothing is selected yet.
 * Purely a viewport hint — it never selects a date.
 *
 * Desktop dual-month calendars resolve this themselves via `startDate` and
 * must not depend on this helper.
 */
export function resolveVerticalCalendarFocusDate(
  focusMonth?: CalendarFocusMonth,
  selectedStart?: CalendarFocusMonth,
): Date | undefined {
  return toCalendarFocusDate(selectedStart) ?? toCalendarFocusDate(focusMonth)
}
